/**
 * Lecture des conditions du contrat de travail portées par la fiche employé.
 *
 * <p>Le contrat affiché dans l'espace de l'employé et sa lettre de démission
 * se composent de ces conditions. Les règles vivent ici pour être vérifiées
 * hors de l'écran.
 */

/**
 * Le contrat est-il conclu pour une durée déterminée ?
 *
 * <p>Le type fait foi ; à défaut de type, une date de fin dit la même chose.
 * L'écran annonçait « une durée indéterminée » à tout le monde, CDD compris.
 *
 * @param {object} emp fiche de l'employé
 * @returns {boolean} vrai pour un contrat à durée déterminée
 */
export function dureeDeterminee(emp) {
  if (!emp) return false;
  if (emp.typeContrat === 'CDD') return true;
  if (emp.typeContrat === 'CDI') return false;
  return !!emp.dateFin;
}

/**
 * Taux d'activité et horaire hebdomadaire, en une phrase, ou rien.
 *
 * @param {object} emp fiche de l'employé
 * @returns {string} la phrase, vide si la fiche n'en dit rien
 */
export function phraseHoraire(emp) {
  const taux = Number(emp?.taux);
  const heures = Number(emp?.hSem);
  // Chaque cas en une phrase entière : l'écran la traduit d'un bloc.
  if (taux > 0 && heures > 0)
    return `L'employé·e travaille à un taux d'activité de ${taux} %, soit ${heures} heures par semaine.`;
  if (taux > 0) return `L'employé·e travaille à un taux d'activité de ${taux} %.`;
  if (heures > 0) return `L'employé·e travaille à ${heures} heures par semaine.`;
  return '';
}

/**
 * La journée du contrat : heures d'arrivée et de départ, pause de midi.
 *
 * <p>La fiche de l'employé fixe ces heures (et c'est sur elles que se calculent
 * ses heures du jour), mais le contrat ne les disait pas.
 *
 * @param {object} emp fiche de l'employé
 * @returns {string} la phrase, vide si la fiche ne fixe pas la journée
 */
export function phraseJournee(emp) {
  const debut = emp?.heureDebut;
  const fin = emp?.heureFin;
  if (!debut || !fin) return '';
  const minutes = Number(emp?.pauseMin ?? 60);
  if (!(minutes > 0)) return `La journée de travail va de ${debut} à ${fin}.`;
  const pause = emp?.pauseDebut || '12:00';
  return `La journée de travail va de ${debut} à ${fin}, avec une pause de midi non payée de ${minutes} minutes dès ${pause}.`;
}

/**
 * Les dispositions finales, comme le contrat officiel les imprime : la
 * convention collective de la fiche s'il y en a une, sinon le Code des
 * obligations.
 *
 * @param {object} emp fiche de l'employé
 * @returns {string} l'article, en une ou deux phrases entières
 */
export function dispositionsFinales(emp) {
  const cct = String(emp?.cct || '').trim();
  const classe = String(emp?.classe || '').trim();
  // Chaque cas en une phrase entière : l'écran la traduit d'un bloc.
  if (!cct)
    return 'Pour le surplus, les dispositions du Code des obligations et, le cas échéant, de la convention collective applicable à la branche font foi. Toute modification du présent contrat requiert la forme écrite.';
  if (!classe)
    return `La convention collective de travail « ${cct} » s'applique ; pour le surplus, les dispositions du Code des obligations font foi. Toute modification du présent contrat requiert la forme écrite.`;
  return `La convention collective de travail « ${cct} » (classe ${classe}) s'applique ; pour le surplus, les dispositions du Code des obligations font foi. Toute modification du présent contrat requiert la forme écrite.`;
}

/**
 * Délai de résiliation de la fiche, en mois ; à défaut, celui de la loi selon
 * les années de service, ou deux mois sans date d'entrée.
 *
 * <p>La lettre de démission lisait `emp.delai`, un champ qui n'existe pas :
 * elle proposait toujours deux mois, quel que soit le délai convenu. Le délai
 * vit dans `delaiResil` (« 1 mois », « 3 mois »…).
 *
 * @param {object} emp fiche de l'employé
 * @param {Date} [le] jour où le congé est donné
 * @returns {number} le délai en mois
 */
export function delaiEnMois(emp, le = new Date()) {
  const s = String(emp?.delaiResil || '').toLowerCase();
  const m = s.match(/(\d+)/);
  if (s.includes('mois') && m) return +m[1];
  // Sans délai convenu, celui de la loi, que le contrat imprime (art. 335c
  // CO) : un mois la première année de service, deux mois de la deuxième à
  // la neuvième, trois ensuite. Deux mois d'office repoussaient d'un mois le
  // dernier jour d'un employé dans sa première année.
  const entree = /^\d{4}-\d{2}-\d{2}/.test(String(emp?.dateEmbauche || ''))
    ? new Date(String(emp.dateEmbauche).slice(0, 10) + 'T00:00:00')
    : null;
  if (!entree || isNaN(entree)) return 2;
  const unAn = new Date(entree);
  unAn.setFullYear(unAn.getFullYear() + 1);
  if (le < unAn) return 1;
  const neufAns = new Date(entree);
  neufAns.setFullYear(neufAns.getFullYear() + 9);
  return le < neufAns ? 2 : 3;
}

/**
 * La date d'effet la plus proche que permet le délai de congé de la loi
 * (art. 335c CO), pour un congé donné ce jour-là : la fin du mois, un mois
 * après la première année de service, deux de la deuxième à la neuvième, trois
 * ensuite. Sans date d'entrée, le plus court, un mois.
 *
 * <p>La lettre de résiliation se disait « dans le respect du délai de congé
 * légal » pour n'importe quelle date, la fin du mois en cours comprise.
 *
 * @param {string} [entree] date d'entrée (AAAA-MM-JJ)
 * @param {Date} [le] jour où le congé est donné
 * @returns {string} la date au plus tôt, AAAA-MM-JJ
 */
export function effetAuPlusTot(entree, le = new Date()) {
  const mois = delaiEnMois({ dateEmbauche: entree || '' }, le);
  const legal = /^\d{4}-\d{2}-\d{2}/.test(String(entree || '')) ? mois : 1;
  const fin = new Date(le.getFullYear(), le.getMonth() + legal + 1, 0);
  return (
    fin.getFullYear() +
    '-' +
    String(fin.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(fin.getDate()).padStart(2, '0')
  );
}
